import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Card,
  CardBody,
  Checkbox,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Spinner,
  Textarea,
} from '@nextui-org/react';
import {
  AlertTriangle,
  ArrowLeft,
  BellRing,
  CheckCircle2,
  Clock,
  Pencil,
  Plus,
  Search,
  Send,
  Trash2,
  Users,
  XCircle,
} from 'lucide-react';
import adminPushCampaignsService, {
  AdminPushUser,
  PushCampaign,
  PushCampaignStats,
} from '../../services/adminPushCampaignsService';

const ACCENT = '#3fced1';

// ---------- Helpers ----------

const formatDateTime = (value?: string | null): string => {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
};

/** Date → Wert für <input type="datetime-local" step="1"> (lokale Zeit). */
const toLocalInputValue = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const statusChip = (status?: string | null) => {
  const map: Record<string, { label: string; className: string }> = {
    scheduled: { label: 'Geplant', className: 'bg-[#3fced1]/15 text-[#3fced1]' },
    sending: { label: 'Wird gesendet …', className: 'bg-amber-500/15 text-amber-400' },
    sent: { label: 'Gesendet', className: 'bg-emerald-500/15 text-emerald-400' },
    cancelled: { label: 'Abgebrochen', className: 'bg-zinc-700 text-gray-300' },
    failed: { label: 'Fehlgeschlagen', className: 'bg-red-500/15 text-red-400' },
  };
  const entry = (status && map[status]) || { label: status || 'Unbekannt', className: 'bg-zinc-700 text-gray-300' };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${entry.className}`}>
      {entry.label}
    </span>
  );
};

const FeedbackBanner: React.FC<{
  message: string | null;
  type: 'success' | 'error';
  onClose: () => void;
}> = ({ message, type, onClose }) => {
  if (!message) return null;
  const isSuccess = type === 'success';
  return (
    <div
      className={`mb-4 flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
        isSuccess
          ? 'border-[#3fced1]/40 bg-[#3fced1]/10 text-[#3fced1]'
          : 'border-red-500/40 bg-red-500/10 text-red-400'
      }`}
    >
      {isSuccess ? <CheckCircle2 size={18} className="mt-0.5 shrink-0" /> : <XCircle size={18} className="mt-0.5 shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} aria-label="Hinweis schließen" className="opacity-70 hover:opacity-100">
        ✕
      </button>
    </div>
  );
};

// =====================================================================
// Kampagnen-Editor (Modal)
// =====================================================================

const CampaignEditorModal: React.FC<{
  isOpen: boolean;
  campaign: PushCampaign | null; // null = neu
  onClose: () => void;
  onSaved: (msg: string) => void;
  onError: (msg: string) => void;
}> = ({ isOpen, campaign, onClose, onSaved, onError }) => {
  const isEditing = !!campaign?._id;

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [targetType, setTargetType] = useState<'all' | 'selected'>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // User fürs Targeting (nur pushbare, d.h. mit notificationId)
  const [users, setUsers] = useState<AdminPushUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');

  // Formular beim Öffnen (re)initialisieren
  useEffect(() => {
    if (!isOpen) return;
    setTitle(campaign?.title || '');
    setMessage(campaign?.message || '');
    setScheduledAt(campaign?.scheduledAt
      ? toLocalInputValue(new Date(campaign.scheduledAt))
      : toLocalInputValue(new Date(Date.now() + 60 * 60 * 1000)));
    setTargetType((campaign?.targetType === 'selected' ? 'selected' : 'all'));
    setSelectedIds(new Set(campaign?.targetPlayerIds ?? []));
    setUserSearch('');
    setValidationError(null);
  }, [isOpen, campaign]);

  // User laden, sobald "Ausgewählte Benutzer" aktiv ist
  useEffect(() => {
    if (!isOpen || targetType !== 'selected' || users.length > 0 || usersLoading) return;
    setUsersLoading(true);
    adminPushCampaignsService
      .listPushableUsers()
      .then(setUsers)
      .catch((err) => onError(adminPushCampaignsService.getErrorMessage(err, 'Benutzer konnten nicht geladen werden.')))
      .finally(() => setUsersLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, targetType]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name ?? '').toLowerCase().includes(q)
      || (u.username ?? '').toLowerCase().includes(q)
      || (u.email ?? '').toLowerCase().includes(q));
  }, [users, userSearch]);

  const toggleUser = (u: AdminPushUser) => {
    if (!u.notificationId) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(u.notificationId!)) next.delete(u.notificationId!);
      else next.add(u.notificationId!);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredUsers.forEach((u) => { if (u.notificationId) next.add(u.notificationId); });
      return next;
    });
  };

  const handleSave = async () => {
    setValidationError(null);
    // Validierungen wie im Flutter-Editor
    if (!title.trim()) return setValidationError('Bitte einen Titel eingeben.');
    if (title.trim().length > 200) return setValidationError('Der Titel darf max. 200 Zeichen lang sein.');
    if (!message.trim()) return setValidationError('Bitte eine Nachricht eingeben.');
    if (message.trim().length > 1000) return setValidationError('Die Nachricht darf max. 1000 Zeichen lang sein.');
    const when = new Date(scheduledAt);
    if (Number.isNaN(when.getTime())) return setValidationError('Bitte einen gültigen Zeitpunkt wählen.');
    if (when.getTime() <= Date.now()) return setValidationError('Der Zeitpunkt muss in der Zukunft liegen.');
    if (targetType === 'selected' && selectedIds.size === 0) {
      return setValidationError('Bitte mindestens einen Benutzer auswählen.');
    }

    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        message: message.trim(),
        scheduledAt: when.toISOString(),
        targetType,
        targetPlayerIds: targetType === 'selected' ? Array.from(selectedIds) : [],
      };
      if (isEditing && campaign?._id) {
        await adminPushCampaignsService.updateCampaign(campaign._id, payload);
        onSaved('Kampagne aktualisiert.');
      } else {
        await adminPushCampaignsService.createCampaign(payload);
        onSaved('Kampagne erstellt – sie wird zum geplanten Zeitpunkt automatisch gesendet.');
      }
      onClose();
    } catch (err) {
      onError(adminPushCampaignsService.getErrorMessage(err, 'Kampagne konnte nicht gespeichert werden.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => !saving && onClose()} placement="center" size="2xl" scrollBehavior="inside">
      <ModalContent className="bg-zinc-900 border border-zinc-800">
        <ModalHeader className="text-white">
          {isEditing ? 'Kampagne bearbeiten' : 'Neue Push-Kampagne'}
        </ModalHeader>
        <ModalBody>
          <Input
            autoFocus
            label="Titel"
            placeholder="Titel der Kampagne"
            value={title}
            onValueChange={setTitle}
            maxLength={200}
            description={`${title.length}/200 Zeichen`}
            classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
          />
          <Textarea
            label="Nachricht"
            placeholder="Die Push-Nachricht …"
            minRows={3}
            value={message}
            onValueChange={setMessage}
            maxLength={1000}
            description={`${message.length}/1000 Zeichen`}
            classNames={{ inputWrapper: 'bg-zinc-800 border border-zinc-700', input: 'text-white', label: 'text-gray-400' }}
          />

          {/* Zeitplanung – sekundengenau (step=1) wie im Flutter-Editor */}
          <div>
            <p className="mb-1.5 text-sm text-gray-400">Versandzeitpunkt (sekundengenau)</p>
            <input
              type="datetime-local"
              step={1}
              value={scheduledAt}
              onChange={(e) => setScheduledAt(e.target.value)}
              className="w-full rounded-xl border border-zinc-700 bg-zinc-800 px-3 py-2.5 text-sm text-white outline-none [color-scheme:dark] focus:border-[#3fced1]"
            />
            <p className="mt-1 text-xs text-gray-500">
              Die Kampagne wird zum geplanten Zeitpunkt automatisch gesendet (Minuten-Cron).
            </p>
          </div>

          {/* Zielgruppe */}
          <RadioGroup
            label="Zielgruppe"
            value={targetType}
            onValueChange={(v) => setTargetType(v as 'all' | 'selected')}
            classNames={{ label: 'text-sm text-gray-400' }}
          >
            <Radio value="all" classNames={{ label: 'text-white text-sm' }}>
              Alle Benutzer
            </Radio>
            <Radio value="selected" classNames={{ label: 'text-white text-sm' }}>
              Ausgewählte Benutzer
            </Radio>
          </RadioGroup>

          {/* User-Auswahl (nur bei "selected") */}
          {targetType === 'selected' && (
            <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {selectedIds.size} Benutzer ausgewählt
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="light" className="text-[#3fced1]" onPress={selectAllFiltered}>
                    Alle auswählen
                  </Button>
                  <Button size="sm" variant="light" className="text-gray-400" onPress={() => setSelectedIds(new Set())}>
                    Keine auswählen
                  </Button>
                </div>
              </div>
              <Input
                size="sm"
                placeholder="Nach Name, Benutzername oder E-Mail suchen …"
                value={userSearch}
                onValueChange={setUserSearch}
                startContent={<Search size={14} className="text-gray-500" />}
                classNames={{ inputWrapper: 'bg-zinc-900 border border-zinc-700', input: 'text-white' }}
              />
              <div className="mt-2 max-h-64 space-y-1 overflow-y-auto">
                {usersLoading ? (
                  <div className="flex justify-center py-6"><Spinner size="sm" color="primary" /></div>
                ) : filteredUsers.length === 0 ? (
                  <p className="px-2 py-4 text-center text-xs text-gray-500">
                    Keine pushbaren Benutzer gefunden (nur Benutzer mit aktivierten Push-Benachrichtigungen wählbar).
                  </p>
                ) : (
                  filteredUsers.map((u) => (
                    <label
                      key={u._id || u.id || u.notificationId}
                      className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-zinc-800/60"
                    >
                      <Checkbox
                        size="sm"
                        isSelected={!!u.notificationId && selectedIds.has(u.notificationId)}
                        onValueChange={() => toggleUser(u)}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-white">{u.name || u.username || 'Unbekannt'}</p>
                        <p className="truncate text-xs text-gray-500">
                          {[u.username, u.email].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          {validationError && (
            <p className="rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {validationError}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose} isDisabled={saving}>Abbrechen</Button>
          <Button
            isLoading={saving}
            className="bg-[#3fced1] font-semibold text-black"
            onPress={handleSave}
          >
            {isEditing ? 'Kampagne aktualisieren' : 'Kampagne erstellen'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// =====================================================================
// Seite
// =====================================================================

const AdminPushCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<PushCampaign[]>([]);
  const [stats, setStats] = useState<PushCampaignStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<PushCampaign | null>(null);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);
  const [confirmSendId, setConfirmSendId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [list, statsData] = await Promise.all([
        adminPushCampaignsService.listCampaigns(100, 0),
        adminPushCampaignsService.getStats().catch(() => null),
      ]);
      setCampaigns(list.campaigns);
      if (statsData) setStats(statsData);
    } catch (err) {
      setError(adminPushCampaignsService.getErrorMessage(err, 'Kampagnen konnten nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingCampaign(null);
    setEditorOpen(true);
  };

  const openEdit = (campaign: PushCampaign) => {
    if (campaign.status !== 'scheduled') {
      setError('Diese Kampagne kann nicht mehr bearbeitet werden.');
      return;
    }
    setEditingCampaign(campaign);
    setEditorOpen(true);
  };

  const handleDelete = async (campaign: PushCampaign) => {
    const id = campaign._id;
    if (!id) return;
    if (confirmDeleteId !== id) {
      setConfirmDeleteId(id);
      setConfirmCancelId(null);
      setConfirmSendId(null);
      return;
    }
    setConfirmDeleteId(null);
    setBusyId(id);
    try {
      await adminPushCampaignsService.deleteCampaign(id);
      setFeedback('Kampagne gelöscht.');
      await load();
    } catch (err) {
      setError(adminPushCampaignsService.getErrorMessage(err, 'Kampagne konnte nicht gelöscht werden.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleCancel = async (campaign: PushCampaign) => {
    const id = campaign._id;
    if (!id) return;
    if (confirmCancelId !== id) {
      setConfirmCancelId(id);
      setConfirmDeleteId(null);
      setConfirmSendId(null);
      return;
    }
    setConfirmCancelId(null);
    setBusyId(id);
    try {
      await adminPushCampaignsService.cancelCampaign(id);
      setFeedback('Kampagne abgebrochen – sie wird nicht mehr gesendet.');
      await load();
    } catch (err) {
      setError(adminPushCampaignsService.getErrorMessage(err, 'Kampagne konnte nicht abgebrochen werden.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleSendNow = async (campaign: PushCampaign) => {
    const id = campaign._id;
    if (!id) return;
    if (confirmSendId !== id) {
      setConfirmSendId(id);
      setConfirmDeleteId(null);
      setConfirmCancelId(null);
      return;
    }
    setConfirmSendId(null);
    setBusyId(id);
    try {
      await adminPushCampaignsService.sendCampaignNow(id);
      setFeedback('Kampagne wurde gesendet.');
      await load();
    } catch (err) {
      setError(adminPushCampaignsService.getErrorMessage(err, 'Kampagne konnte nicht gesendet werden.'));
    } finally {
      setBusyId(null);
    }
  };

  const statCards: { label: string; value: number }[] = [
    { label: 'Gesamt', value: stats?.total ?? campaigns.length },
    { label: 'Geplant', value: stats?.scheduled ?? 0 },
    { label: 'Gesendet', value: stats?.sent ?? 0 },
    { label: 'Fehlgeschlagen', value: stats?.failed ?? 0 },
    { label: 'Abgebrochen', value: stats?.cancelled ?? 0 },
  ];

  return (
    <>
      <Helmet>
        <title>Push-Kampagnen | Admin | DartCloud</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
        <div className="mb-6 flex items-center gap-3">
          <Button
            as={RouterLink}
            to="/admin"
            isIconOnly
            variant="bordered"
            className="border-zinc-700 text-gray-300"
            aria-label="Zurück zum Admin-Dashboard"
          >
            <ArrowLeft size={18} />
          </Button>
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 text-2xl font-extrabold tracking-tight text-white">
              <BellRing size={22} style={{ color: ACCENT }} />
              Push-Kampagnen
            </h1>
            <p className="text-sm text-gray-400">
              Push-Benachrichtigungen planen, senden und auswerten.
            </p>
          </div>
          <Button
            className="bg-[#3fced1] font-semibold text-black"
            startContent={<Plus size={16} />}
            onPress={openCreate}
          >
            Neue Kampagne
          </Button>
        </div>

        <FeedbackBanner message={feedback} type="success" onClose={() => setFeedback(null)} />
        <FeedbackBanner message={error} type="error" onClose={() => setError(null)} />

        {/* Stats */}
        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          {statCards.map((card) => (
            <Card key={card.label} className="border border-zinc-800 bg-zinc-900">
              <CardBody className="p-3.5 text-center">
                <p className="text-xl font-extrabold text-white">{card.value}</p>
                <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                  {card.label}
                </p>
              </CardBody>
            </Card>
          ))}
        </div>

        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center">
            <Spinner size="lg" color="primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign) => {
              const id = campaign._id || '';
              const result = adminPushCampaignsService.normalizeResult(campaign);
              const isScheduled = campaign.status === 'scheduled';
              const canDelete = campaign.status !== 'sent' && campaign.status !== 'sending';
              return (
                <Card key={id} className="border border-zinc-800 bg-zinc-900">
                  <CardBody className="p-4 sm:p-5">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-base font-bold text-white">{campaign.title}</h3>
                      {statusChip(campaign.status)}
                    </div>

                    <p className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                      <Clock size={12} />
                      Geplant für {formatDateTime(campaign.scheduledAt)}
                      <span className="mx-1">·</span>
                      <Users size={12} />
                      {campaign.targetType === 'selected'
                        ? `${(campaign.targetPlayerIds ?? []).length} ausgewählte Benutzer`
                        : 'Alle Benutzer'}
                    </p>

                    <p className="mt-2 whitespace-pre-line rounded-xl bg-zinc-950/60 px-3 py-2 text-sm text-gray-300">
                      {campaign.message}
                    </p>

                    {result && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span>Empfänger: <b className="text-white">{result.recipientCount}</b></span>
                        <span>Erfolgreich: <b className="text-emerald-400">{result.successCount}</b></span>
                        {result.failedCount > 0 && (
                          <span>Fehlgeschlagen: <b className="text-red-400">{result.failedCount}</b></span>
                        )}
                        {result.sentAt && <span>Gesendet: {formatDateTime(result.sentAt)}</span>}
                        {result.error && (
                          <span className="flex items-center gap-1 text-red-400">
                            <AlertTriangle size={12} /> {result.error}
                          </span>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-zinc-800 pt-3">
                      {isScheduled && (
                        <>
                          <Button
                            size="sm"
                            variant="bordered"
                            className="border-zinc-700 text-gray-300"
                            startContent={<Pencil size={13} />}
                            onPress={() => openEdit(campaign)}
                          >
                            Bearbeiten
                          </Button>
                          <Button
                            size="sm"
                            isLoading={busyId === id && confirmSendId === null}
                            className={confirmSendId === id
                              ? 'bg-red-500 font-semibold text-white'
                              : 'bg-[#3fced1]/15 font-semibold text-[#3fced1]'}
                            startContent={<Send size={13} />}
                            onPress={() => handleSendNow(campaign)}
                          >
                            {confirmSendId === id ? 'Wirklich JETZT senden?' : 'Jetzt senden'}
                          </Button>
                          <Button
                            size="sm"
                            variant="bordered"
                            className={confirmCancelId === id
                              ? 'border-red-500 text-red-400'
                              : 'border-zinc-700 text-gray-300'}
                            startContent={<XCircle size={13} />}
                            onPress={() => handleCancel(campaign)}
                          >
                            {confirmCancelId === id ? 'Wirklich abbrechen?' : 'Abbrechen'}
                          </Button>
                        </>
                      )}
                      <div className="flex-1" />
                      {canDelete && (
                        <Button
                          isIconOnly
                          size="sm"
                          variant="light"
                          aria-label="Kampagne löschen"
                          className={confirmDeleteId === id ? 'text-red-400' : 'text-gray-500 hover:text-red-400'}
                          onPress={() => handleDelete(campaign)}
                        >
                          <Trash2 size={15} />
                        </Button>
                      )}
                    </div>
                    {confirmDeleteId === id && (
                      <p className="mt-2 text-xs text-red-400">
                        Wirklich löschen? Erneut auf den Papierkorb klicken zum Bestätigen.
                      </p>
                    )}
                  </CardBody>
                </Card>
              );
            })}

            {campaigns.length === 0 && (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <BellRing size={40} className="text-zinc-700" />
                <p className="text-gray-500">Noch keine Push-Kampagnen. Lege die erste an!</p>
                <Button
                  className="bg-[#3fced1] font-semibold text-black"
                  startContent={<Plus size={16} />}
                  onPress={openCreate}
                >
                  Neue Kampagne
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <CampaignEditorModal
        isOpen={editorOpen}
        campaign={editingCampaign}
        onClose={() => setEditorOpen(false)}
        onSaved={(msg) => {
          setFeedback(msg);
          load();
        }}
        onError={setError}
      />
    </>
  );
};

export default AdminPushCampaignsPage;
